import React, { useMemo, useState } from 'react';
import { Box, Chip, FormControl, InputAdornment, MenuItem, Select, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography, useMediaQuery, useTheme, Switch, Button } from '@mui/material';
import { Icon } from '@iconify/react';
import TopBar from '../components/TopBar';
import Sidebar from '../components/Sidebar';
interface AlertConfigurationProps {
  onLogout: () => void;
}
type AlertRow = {
  event: string;
  note?: string;
  types: string[];
  stage: string;
  recipient: string;
  sms: string;
  smsAllowed: boolean;
  smsOn: boolean;
  email: string;
  emailOn: boolean;
};
const rows: AlertRow[] = [{
  event: 'Transaction under compliance review',
  types: ['ALL'],
  stage: 'Screening Failed',
  recipient: 'Beneficiary',
  sms: 'Your transaction {txn_id} is under compliance review. Please contact your remitter.',
  smsAllowed: true,
  smsOn: true,
  email: 'Dear {beneficiary}, your remittance {txn_id} is currently under compliance review.',
  emailOn: true
}, {
  event: 'Transaction stuck in compliance screen for X days',
  note: 'X days configurable',
  types: ['ALL'],
  stage: 'Compliance Screen Overdue',
  recipient: 'Bank HRB & Ops',
  sms: '',
  smsAllowed: false,
  smsOn: false,
  email: '[Alert] Transaction {txn_id} has been stuck in the Backoffice Compliance Screen for {days} days.',
  emailOn: true
}, {
  event: 'Transaction cancelled after Title Fetch failure',
  types: ['FT', 'IBFT'],
  stage: 'Title Fetch Failed',
  recipient: 'Partner/MTO',
  sms: '',
  smsAllowed: false,
  smsOn: false,
  email: 'Dear {partner}, transaction {txn_id} has been cancelled following a Title Fetch failure.',
  emailOn: true
}, {
  event: 'Cash Over Counter available for payout',
  types: ['COC'],
  stage: 'Txn Downloaded',
  recipient: 'Beneficiary',
  sms: 'Your cash remittance {txn_id} is ready for collection.',
  smsAllowed: true,
  smsOn: true,
  email: 'Dear {beneficiary}, your cash remittance is available for over-the-counter collection.',
  emailOn: true
}, {
  event: 'CBS is down - transactions stopped posting',
  types: ['ALL'],
  stage: 'CBS No Response',
  recipient: 'Bank HRB & Ops',
  sms: '',
  smsAllowed: false,
  smsOn: false,
  email: '[Alert] CBS is not responding and transactions have stopped posting. Immediate action is required.',
  emailOn: true
}];
const typeColor = (type: string) => {
  if (type === 'FT') return {
    bgcolor: 'rgba(14,165,233,0.14)',
    color: '#38BDF8'
  };
  if (type === 'IBFT') return {
    bgcolor: 'rgba(236,72,153,0.14)',
    color: '#F472B6'
  };
  if (type === 'COC') return {
    bgcolor: 'rgba(16,185,129,0.14)',
    color: '#34D399'
  };
  return {
    bgcolor: 'rgba(148,163,184,0.16)',
    color: 'text.secondary'
  };
};
const AlertConfiguration: React.FC<AlertConfigurationProps> = ({
  onLogout
}) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [type, setType] = useState('');
  const [stage, setStage] = useState('');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDark = theme.palette.mode === 'dark';
  const filteredRows = useMemo(() => rows.filter(row => {
    const matchQuery = !query || row.event.toLowerCase().includes(query.toLowerCase()) || row.stage.toLowerCase().includes(query.toLowerCase());
    const matchType = !type || row.types.includes(type);
    const matchStage = !stage || row.stage === stage;
    return matchQuery && matchType && matchStage;
  }), [query, type, stage]);
  const stages = Array.from(new Set(rows.map(row => row.stage)));
  const activeCount = rows.filter(row => row.smsOn || row.emailOn).length;
  return <Box sx={{
    display: 'flex',
    width: '100%',
    minHeight: '100vh',
    bgcolor: 'background.default',
    overflow: 'hidden'
  }} data-id="element-2911" data-mp="65d7u7eiso5g">
      <TopBar onMenuClick={() => setMobileOpen(value => !value)} onLogout={onLogout} data-id="element-2912" data-mp="2igy5v1bw0bht" />
      <Sidebar open={mobileOpen} onClose={() => setMobileOpen(false)} activePage="alert-configuration" data-id="element-2913" data-mp="2iele5wgbumrk" />

      <Box component="main" sx={{
      flexGrow: 1,
      width: {
        xs: '100%',
        sm: 'calc(100% - 240px)'
      },
      maxWidth: '100%',
      ml: 0,
      mt: 8,
      py: 2,
      px: {
        xs: 2,
        sm: 3,
        md: 5
      },
      overflow: 'hidden'
    }} data-id="element-2914" data-mp="2dk7pse031fwe">
        <Box sx={{
        display: 'flex',
        alignItems: 'center',
        mb: 2
      }} data-id="element-2915" data-mp="24j9mxz1drefe">
          <Icon icon="lucide:bell-ring" width={28} height={28} color="#5B4EF0" data-id="element-2916" data-mp="3165w84iu3cqq" />
          <Typography variant="h5" component="h1" sx={{
          ml: 1.5,
          fontWeight: 700
        }} data-id="element-2917" data-mp="2x5m0n5dwsh6p">
            Alert Configuration
          </Typography>
        </Box>

        <Box sx={{
        borderRadius: 3,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: 'background.paper',
        overflow: 'hidden',
        boxShadow: isDark ? '0 18px 44px rgba(0,0,0,0.30)' : '0 12px 28px rgba(15,23,42,0.08)'
      }} data-id="element-2918" data-mp="2scsse7j8eqpv">
          <Box sx={{
          px: 2.5,
          py: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
          display: 'flex',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap'
        }} data-id="element-2919" data-mp="3g4zuzofjvtxq">
            <Box data-id="element-2920" data-mp="3nhz6ssuxj8ul">
              <Typography sx={{
              fontWeight: 700
            }} data-id="element-2921" data-mp="3sexyl4r4f3pm">Alert Events</Typography>
              <Typography variant="caption" color="text.secondary" data-id="element-2922" data-mp="3sh1zrexmxom7">{activeCount} active notifications</Typography>
            </Box>
            <Box sx={{
            display: 'flex',
            gap: 1.25,
            flexWrap: 'wrap'
          }} data-id="element-2923" data-mp="59zz36i67jze">
              <TextField size="small" value={query} onChange={event => setQuery(event.target.value)} placeholder="Search events" InputProps={{
              startAdornment: <InputAdornment position="start" data-id="element-2925" data-mp="jze98gtkm5fu"><Icon icon="lucide:search" width={16} data-id="element-2926" data-mp="k0gx0fvljrrj" /></InputAdornment>
            }} sx={{
              minWidth: 220
            }} data-id="element-2924" data-mp="57wwlmztzyds" />
              <FormControl size="small" sx={{
              minWidth: 130
            }} data-id="element-2927" data-mp="p0bniibgmfc2">
                <Select displayEmpty value={type} onChange={event => setType(event.target.value)} data-id="element-2928" data-mp="t0vjvjo7lex5">
                  <MenuItem value="" data-id="element-2929" data-mp="xzozbhn23fzy">All types</MenuItem>
                  <MenuItem value="FT" data-id="element-2930" data-mp="2u21vxooe82kr">FT</MenuItem>
                  <MenuItem value="IBFT" data-id="element-2931" data-mp="2u2lbiyqkmevm">IBFT</MenuItem>
                  <MenuItem value="COC" data-id="element-2932" data-mp="2q0q91qwwlvg9">COC</MenuItem>
                  <MenuItem value="ALL" data-id="element-2933" data-mp="2q2b7hrmc9yje">ALL</MenuItem>
                </Select>
              </FormControl>
              <FormControl size="small" sx={{
              minWidth: 210
            }} data-id="element-2934" data-mp="33t6awoe2jnfl">
                <Select displayEmpty value={stage} onChange={event => setStage(event.target.value)} data-id="element-2935" data-mp="2z03meuo5f469">
                  <MenuItem value="" data-id="element-2936" data-mp="3doq1cv9v5twy">All stages</MenuItem>
                  {stages.map(item => <MenuItem key={item} value={item} data-id="element-2937" data-mp="38rr8eg1xrt0c">{item}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
          </Box>

          <TableContainer sx={{
          overflowX: 'auto'
        }} data-id="element-2938" data-mp="3momt55rjv8w8">
            <Table stickyHeader sx={{
            minWidth: 1120
          }} data-id="element-2939" data-mp="3hsper4zukcm3">
              <TableHead data-id="element-2940" data-mp="1scq0j9hdfwzl">
                <TableRow data-id="element-2941" data-mp="1sbwp3v7gwjb4">
                  {['Event', 'Txn Type', 'Trigger Stage', 'Recipient', 'SMS Template', 'SMS', 'Email Template', 'Email'].map(header => <TableCell key={header} sx={{
                  fontWeight: 800,
                  color: 'text.secondary',
                  fontSize: '0.72rem',
                  textTransform: 'uppercase',
                  bgcolor: isDark ? '#201D3C' : '#F8FAFC'
                }} data-id="element-2942" data-mp="2vkofm473202">{header}</TableCell>)}
                </TableRow>
              </TableHead>
              <TableBody data-id="element-2943" data-mp="3q8eoviah7451">
                {filteredRows.map(row => <TableRow key={`${row.event}-${row.recipient}`} hover data-id="element-2944" data-mp="3u8xhvfo3ug8t">
                    <TableCell sx={{
                  minWidth: 250
                }} data-id="element-2945" data-mp="3g9xs24ouvvb2">
                      <Typography sx={{
                    fontWeight: 650,
                    fontSize: '0.84rem'
                  }} data-id="element-2946" data-mp="3gcjz837u48sr">{row.event}</Typography>
                      {row.note && <Typography variant="caption" color="text.secondary" data-id="element-2947" data-mp="379sl1vnkqyxf">{row.note}</Typography>}
                    </TableCell>
                    <TableCell data-id="element-2948" data-mp="3bam9cq4xifqu">
                      <Box sx={{
                    display: 'flex',
                    gap: 0.5,
                    flexWrap: 'wrap'
                  }} data-id="element-2949" data-mp="3bbw87hjlvryj">
                        {row.types.map(item => <Chip key={item} label={item} size="small" sx={{
                      height: 22,
                      fontWeight: 800,
                      fontSize: '0.66rem',
                      ...typeColor(item)
                    }} data-id="element-2950" data-mp="15j7cijn12ssp" />)}
                      </Box>
                    </TableCell>
                    <TableCell data-id="element-2951" data-mp="3ti4rw8ea9zcv"><Chip label={row.stage} size="small" sx={{
                    fontWeight: 650,
                    bgcolor: isDark ? 'rgba(255,255,255,0.06)' : '#F1F5F9'
                  }} data-id="element-2952" data-mp="3thwg6jtwd039" /></TableCell>
                    <TableCell data-id="element-2953" data-mp="3jp77txtn82c5"><Chip label={row.recipient} size="small" color="primary" variant="outlined" data-id="element-2954" data-mp="3joxogt33stff" /></TableCell>
                    <TableCell sx={{
                  maxWidth: 260
                }} data-id="element-2955" data-mp="3eqmdgf12ocvm">
                      {row.smsAllowed ? <><Typography sx={{
                      fontSize: '0.78rem',
                      color: 'text.secondary'
                    }} data-id="element-2956" data-mp="3aqlow1k9elau">{row.sms}</Typography><Button size="small" sx={{
                      mt: 0.5
                    }} data-id="element-2957" data-mp="35q7s5fo3yxs4">Edit</Button></> : <Typography variant="caption" color="text.secondary" fontStyle="italic" data-id="element-2958" data-mp="35su03gewvfav">Email only</Typography>}
                    </TableCell>
                    <TableCell data-id="element-2959" data-mp="2vtcvz3fb59h0"><Switch size="small" checked={row.smsOn} disabled={!row.smsAllowed} data-id="element-2960" data-mp="2vtl98wfcegsq" /></TableCell>
                    <TableCell sx={{
                  maxWidth: 300
                }} data-id="element-2961" data-mp="yyq9378pg790"><Typography sx={{
                    fontSize: '0.78rem',
                    color: 'text.secondary'
                  }} data-id="element-2962" data-mp="yxx68hcva1w5">{row.email}</Typography><Button size="small" sx={{
                    mt: 0.5
                  }} data-id="element-2963" data-mp="u1q46znldajh">Edit</Button></TableCell>
                    <TableCell data-id="element-2964" data-mp="12hfk5tkwiwac"><Switch size="small" checked={row.emailOn} data-id="element-2965" data-mp="12hp0epg59p2u" /></TableCell>
                  </TableRow>)}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    </Box>;
};
export default AlertConfiguration;